import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { AddressType } from '@sg/garnish';
import {
  BodyText,
  Ledger as LedgerComponent,
} from '@sg/garnish';
import type { CourierDetails } from '@sg/graphql-schema';
import type { Ledger } from '@sg/graphql-schema';
import {
  type DeliveryPreferenceType,
  type FlattenedOrderStatuses,
} from '@sg/graphql-schema';

import { useLocalizationContext } from '@order/Localization';

import { type PartialLineItem } from '../../../../graphql';
import { useLedgerLineItems } from '../../hooks';
import { OrderStatusLineItems } from '../OrderStatusLineItems';

export const OrderStatusLedger = (props: OrderStatusDetailsProps) => {
  const { lineItems, ledger } = props;
  const { t } = useLocalizationContext();

  const ledgerTip = ledger.tip ?? 0;
  const ledgerItems = useLedgerLineItems({
    ledger,
    showTip: ledgerTip > 0,
    showTotal: true,
    showTaxes: true,
    showReward: true,
  });

  return (
    <View>
      <View>
        <BodyText sizeMatch={['16']} bold>
          {t('general.your-order')}
        </BodyText>

        <OrderStatusLineItems lineItems={lineItems} />
      </View>

      <LedgerComponent
        style={styles.ledger}
        testID="order-status-details-ledger"
        hasTopBorder={true}
        items={ledgerItems}
      />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ledger: {
    paddingBottom: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusDetailsProps = Readonly<{
  orderType: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  deliveryAddressName?: string;
  deliveryAddressStreet?: string;
  deliveryAddressSecondaryStreet?: string;
  deliveryAddressCity?: string;
  deliveryAddressState?: string;
  deliveryAddressZipCode?: string;
  deliveryAddressNotes?: string;
  deliveryPreference?: DeliveryPreferenceType;
  locationName?: string;
  locationAddress?: string;
  locationCity?: string;
  locationState?: string;
  locationZipCode?: string;
  lineItems: readonly PartialLineItem[];
  dropoffLocation?: string;
  ledger: Partial<Ledger>;
  courierName?: CourierDetails['name'];
  courierPhone?: CourierDetails['phone'];
}>;
